import { Loader2, PhoneCall } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { getPrioritizedPhoneHandles } from '@/components/treatment/v3/request/modals/utils/phoneHandleUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { useGetMedicalProviderQuery } from '@/services/api/treatmentService';
import { Task } from '@/services/types/client-intake-types';
import { ContactHandle } from '@/services/types/treatment-types';
import { isPhoneNumberValid } from '@/utils/validation';

const CUSTOM_OPTION = '__CUSTOM__';
const NONE_OPTION = '__NONE__';

const formatPhoneNumber = (phone: string) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return phone;
};

interface VoiceAgentContactMethodCardProps {
  task: Task;
  matterId: string;
  onContactNumberChange?: (value: string | null) => void;
}

interface ContactOption {
  value: string;
  label: string;
  description?: string;
  badge?: string;
}

export const VoiceAgentContactMethodCard: React.FC<
  VoiceAgentContactMethodCardProps
> = ({ task, matterId, onContactNumberChange }) => {
  const treatmentRequest = task.treatment_record_request;
  const providerId = treatmentRequest?.provider;
  const requestId = treatmentRequest?.id;

  const hasRequestContext = Boolean(matterId && providerId && requestId);

  const { data: request, isLoading: isRequestLoading } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsRetrieveQuery(
      {
        matterId,
        providerId: providerId || '',
        requestId: requestId || '',
      },
      {
        skip: !hasRequestContext,
      },
    );

  const { data: provider } = useGetMedicalProviderQuery(
    {
      matterId: matterId || '',
      providerId: providerId || '',
    },
    {
      skip: !hasRequestContext,
    },
  );

  const [updateChannel, { isLoading: isUpdating }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsUpdateMutation();

  const phoneHandles = useMemo(() => {
    const handles: ContactHandle[] | undefined = provider?.handles;
    return getPrioritizedPhoneHandles(handles);
  }, [provider?.handles]);

  const phoneOptions: ContactOption[] = useMemo(() => {
    const options: ContactOption[] = [];
    const seen = new Set<string>();

    const pushOption = (
      value: string,
      label: string,
      description?: string,
      badge?: string,
    ) => {
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({ value, label, description, badge });
    };

    if (request?.channel?.phone_number) {
      pushOption(
        request.channel.phone_number,
        'Current number',
        formatPhoneNumber(request.channel.phone_number),
      );
    }

    phoneHandles.forEach((handle) => {
      if (!handle?.value) return;
      pushOption(
        handle.value,
        handle.name || 'Phone',
        formatPhoneNumber(handle.value),
        handle.isPriority ? 'Records/Billing' : undefined,
      );
    });

    return options;
  }, [phoneHandles, request?.channel?.phone_number]);

  const [selectValue, setSelectValue] = useState<string>(NONE_OPTION);
  const [customValue, setCustomValue] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const channelId = request?.channel?.id;
  const channelPhoneNumber = request?.channel?.phone_number;

  useEffect(() => {
    if (!channelId) return;
    const current = channelPhoneNumber ?? null;
    setSelectValue(current || NONE_OPTION);
    setShowCustomInput(false);
    setCustomValue('');
    onContactNumberChange?.(current);
  }, [channelId, channelPhoneNumber, onContactNumberChange]);

  const persistPhoneNumber = useCallback(
    async (value: string | null) => {
      if (!channelId || !providerId || !requestId) {
        toast.error('Missing request channel details');
        return;
      }

      try {
        await updateChannel({
          matterId,
          providerId,
          requestId,
          channelId: channelId as string,
          updateTreatmentRecordRequestChannelSchema: {
            phone_number: value,
          },
        }).unwrap();
        setSelectValue(value || NONE_OPTION);
        onContactNumberChange?.(value);
        if (value) {
          toast.success('Contact number saved');
        } else {
          toast.success('Contact number cleared');
        }
      } catch (error) {
        toast.error('Failed to update contact number');
      }
    },
    [
      matterId,
      providerId,
      requestId,
      channelId,
      updateChannel,
      onContactNumberChange,
    ],
  );

  const handleSelectChange = (value: string) => {
    if (value === CUSTOM_OPTION) {
      setShowCustomInput(true);
      setCustomValue('');
      return;
    }

    setShowCustomInput(false);
    setCustomValue('');

    if (value === NONE_OPTION) {
      persistPhoneNumber(null);
      return;
    }

    persistPhoneNumber(value);
  };

  const handleSaveCustom = async () => {
    const trimmed = customValue.trim();
    if (!trimmed) return;
    await persistPhoneNumber(trimmed);
    setShowCustomInput(false);
    setCustomValue('');
  };

  if (!hasRequestContext) {
    return null;
  }

  let body: React.ReactNode;
  if (isRequestLoading) {
    body = (
      <div className="space-y-2">
        <div className="h-10 bg-gray-100 rounded-md animate-pulse" />
        <div className="h-4 bg-gray-100 rounded w-2/3 animate-pulse" />
      </div>
    );
  } else if (!request?.channel) {
    body = (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        Add a contact channel for this request on the HIPAA Package tab to
        enable Voice AI calling.
      </div>
    );
  } else {
    body = (
      <div className="space-y-3">
        {phoneOptions.length > 0 && (
          <div className="space-y-2">
            <Label className="text-xs text-gray-600">Directory Contacts</Label>
            <Select
              value={showCustomInput ? CUSTOM_OPTION : selectValue}
              onValueChange={handleSelectChange}
              disabled={isUpdating}
            >
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Select a contact" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {phoneOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">
                        {option.label}
                        {option.badge && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                            {option.badge}
                          </span>
                        )}
                      </span>
                      {option.description && (
                        <span className="text-xs text-gray-500">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM_OPTION}>
                  Enter custom number
                </SelectItem>
                <SelectItem value={NONE_OPTION}>No phone number</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {(phoneOptions.length === 0 || showCustomInput) && (
          <div className="space-y-2">
            <Label
              className="text-xs text-gray-600"
              htmlFor="voice-ai-contact-custom"
            >
              Custom Phone Number
            </Label>
            <Input
              id="voice-ai-contact-custom"
              type="tel"
              value={customValue}
              onChange={(event) => setCustomValue(event.target.value)}
              placeholder="Enter phone number (e.g. 303-555-1234)"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSaveCustom}
                disabled={!isPhoneNumberValid(customValue.trim()) || isUpdating}
              >
                Save Number
              </Button>
              {showCustomInput && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowCustomInput(false);
                    setCustomValue('');
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
            {!isPhoneNumberValid(customValue.trim()) && customValue && (
              <p className="text-xs text-red-600">
                Enter a valid phone number (e.g. 303-555-1234 or +12025550123)
              </p>
            )}
          </div>
        )}

        {selectValue !== NONE_OPTION && (
          <div className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
            <PhoneCall className="h-3.5 w-3.5" />
            <span>
              Current Voice AI contact:{' '}
              <span className="font-semibold">
                {formatPhoneNumber(selectValue)}
              </span>
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            Contact Method
          </div>
          <div className="text-xs text-gray-500">
            Choose the phone number Voice AI should dial
          </div>
        </div>
        {isUpdating && (
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        )}
      </div>
      {body}
    </div>
  );
};
